//import React, { createContext, useContext, useState, useEffect } from 'react';

const UserContext = React.createContext();

export const useUser = () => {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const [orderHistory, setOrderHistory] = React.useState([]);
  const [favoriteItems, setFavoriteItems] = React.useState([]);
  const [preferences, setPreferences] = React.useState({
    dietaryRestrictions: [],
    favoriteCategories: [],
    dislikedItems: [],
    defaultSize: null,
    spiceLevel: null,
  });
  const [tasteProfile, setTasteProfile] = React.useState({
    proteinPreferences: [],
    flavorProfile: [], // spicy, mild, sweet, savory, etc.
    cuisinePreferences: [],
  });

  // Load user data from localStorage on mount
  React.useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedOrderHistory = localStorage.getItem('orderHistory');
    const savedFavorites = localStorage.getItem('favoriteItems');
    const savedPreferences = localStorage.getItem('userPreferences');
    const savedTasteProfile = localStorage.getItem('tasteProfile');

    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }

    if (savedOrderHistory) {
      try {
        const history = JSON.parse(savedOrderHistory);
        setOrderHistory(history || []);
      } catch (e) {
        console.error('Error parsing order history:', e);
      }
    }

    if (savedFavorites) {
      try {
        const favorites = JSON.parse(savedFavorites);
        setFavoriteItems(favorites || []);
      } catch (e) {
        console.error('Error parsing favorites:', e);
      }
    }

    if (savedPreferences) {
      try {
        setPreferences(JSON.parse(savedPreferences));
      } catch (e) {
        console.error('Error parsing preferences:', e);
      }
    }

    if (savedTasteProfile) {
      try {
        setTasteProfile(JSON.parse(savedTasteProfile));
      } catch (e) {
        console.error('Error parsing taste profile:', e);
      }
    }
  }, []);

  // Save user data to localStorage when it changes
  React.useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  React.useEffect(() => {
    if (orderHistory.length > 0) {
      localStorage.setItem('orderHistory', JSON.stringify(orderHistory));
    } else {
      localStorage.removeItem('orderHistory');
    }
  }, [orderHistory]);

  React.useEffect(() => {
    if (favoriteItems.length > 0) {
      localStorage.setItem('favoriteItems', JSON.stringify(favoriteItems));
    } else {
      localStorage.removeItem('favoriteItems');
    }
  }, [favoriteItems]);

  React.useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences]);

  React.useEffect(() => {
    localStorage.setItem('tasteProfile', JSON.stringify(tasteProfile));
  }, [tasteProfile]);

  // Login/logout functions
  const login = (userData) => {
    setUser({
      id: userData.id,
      email: userData.email,
      name: userData.name,
      phone: userData.phone,
      isAuthenticated: true,
    });
  };

  const logout = () => {
    setUser(null);
    // Keep order history and preferences for guest users
  };

  const updateUser = (updates) => {
    setUser((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  // Order history management
  const addOrderToHistory = (order) => {
    const newOrder = {
      id: order.id || order.orderNumber,
      orderNumber: order.orderNumber,
      Business: order.Business,
      items: order.items,
      totalcost: order.totalcost,
      status: order.status,
      tstamp: order.tstamp || new Date().toISOString(),
      deliverType: order.deliverType,
      destination: order.destination,
    };

    setOrderHistory((prev) => {
      // Check if order already exists
      const exists = prev.some((o) => o.id === newOrder.id || o.orderNumber === newOrder.orderNumber);
      if (exists) {
        return prev;
      }
      return [newOrder, ...prev].slice(0, 50); // Keep last 50 orders
    });

    // Update taste profile based on order
    updateTasteProfileFromOrder(newOrder);
  };

  const getRecentOrders = (limit = 10) => {
    return orderHistory.slice(0, limit);
  };

  const getOrdersForBusiness = (businessId) => {
    return orderHistory.filter((order) => order.Business?.id === businessId);
  };

  // Favorite items management
  const addFavoriteItem = (item) => {
    setFavoriteItems((prev) => {
      const exists = prev.some((fav) => fav.id === item.id);
      if (exists) {
        return prev;
      }
      return [...prev, item];
    });
  };

  const removeFavoriteItem = (itemId) => {
    setFavoriteItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const isFavoriteItem = (itemId) => {
    return favoriteItems.some((item) => item.id === itemId);
  };

  // Preferences management
  const updatePreferences = (updates) => {
    setPreferences((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const addDietaryRestriction = (restriction) => {
    setPreferences((prev) => ({
      ...prev,
      dietaryRestrictions: [...prev.dietaryRestrictions, restriction],
    }));
  };

  const removeDietaryRestriction = (restriction) => {
    setPreferences((prev) => ({
      ...prev,
      dietaryRestrictions: prev.dietaryRestrictions.filter((r) => r !== restriction),
    }));
  };

  const addFavoriteCategory = (category) => {
    setPreferences((prev) => ({
      ...prev,
      favoriteCategories: [...prev.favoriteCategories, category],
    }));
  };

  // Taste profile management
  const updateTasteProfileFromOrder = (order) => {
    if (!order.items || order.items.length === 0) return;

    const newProteinPreferences = [...tasteProfile.proteinPreferences];
    const newFlavorProfile = [...tasteProfile.flavorProfile];

    order.items.forEach((item) => {
      // Extract protein preferences from item names and descriptions
      const proteins = ['chicken', 'steak', 'beef', 'pork', 'fish', 'vegetarian', 'vegan', 'tofu'];
      proteins.forEach((protein) => {
        const itemText = `${item.name} ${item.description || ''}`.toLowerCase();
        if (itemText.includes(protein) && !newProteinPreferences.includes(protein)) {
          newProteinPreferences.push(protein);
        }
      });

      // Extract flavor preferences
      const flavors = ['spicy', 'mild', 'sweet', 'savory', 'tangy', 'creamy'];
      flavors.forEach((flavor) => {
        const itemText = `${item.name} ${item.description || ''}`.toLowerCase();
        if (itemText.includes(flavor) && !newFlavorProfile.includes(flavor)) {
          newFlavorProfile.push(flavor);
        }
      });
    });

    setTasteProfile((prev) => ({
      ...prev,
      proteinPreferences: newProteinPreferences,
      flavorProfile: newFlavorProfile,
    }));
  };

  // Personalization helpers
  const getPersonalizedRecommendations = (menuItems, limit = 5) => {
    if (!menuItems || menuItems.length === 0) return [];

    const scores = menuItems.map((item) => ({
      item,
      score: calculateItemScore(item),
    }));

    // Sort by score and return top items
    scores.sort((a, b) => b.score - a.score);
    return scores.slice(0, limit).map((s) => s.item);
  };

  const calculateItemScore = (item) => {
    let score = 0;

    // Check if item is a favorite
    if (isFavoriteItem(item.id)) {
      score += 50;
    }

    // Check if item matches favorite categories
    if (preferences.favoriteCategories.some((cat) =>
      item.Category?.name?.toLowerCase().includes(cat.toLowerCase())
    )) {
      score += 30;
    }

    // Check if item matches taste profile
    const itemText = `${item.name} ${item.description || ''}`.toLowerCase();
    tasteProfile.proteinPreferences.forEach((protein) => {
      if (itemText.includes(protein)) {
        score += 20;
      }
    });

    tasteProfile.flavorProfile.forEach((flavor) => {
      if (itemText.includes(flavor)) {
        score += 15;
      }
    });

    // Check if item has been ordered before
    const orderCount = orderHistory.filter((order) =>
      order.items?.some((orderItem) => orderItem.product_id === item.id)
    ).length;
    score += orderCount * 10;

    return score;
  };

  const shouldShowItem = (item) => {
    // Filter out items with disliked ingredients
    if (!item) return true;

    const itemText = `${item.name} ${item.description || ''}`.toLowerCase();

    // Check dietary restrictions
    if (preferences.dietaryRestrictions.includes('vegan')) {
      const nonVegan = ['chicken', 'beef', 'pork', 'fish', 'cheese', 'milk', 'egg'];
      if (nonVegan.some((ingredient) => itemText.includes(ingredient))) {
        return false;
      }
    }

    if (preferences.dietaryRestrictions.includes('vegetarian')) {
      const nonVegetarian = ['chicken', 'beef', 'pork', 'fish', 'meat'];
      if (nonVegetarian.some((ingredient) => itemText.includes(ingredient))) {
        return false;
      }
    }

    // Check disliked items
    if (preferences.dislikedItems.some((disliked) =>
      itemText.includes(disliked.toLowerCase())
    )) {
      return false;
    }

    return true;
  };

  const getOrderFrequency = (productId) => {
    return orderHistory.filter((order) =>
      order.items?.some((orderItem) => orderItem.product_id === productId)
    ).length;
  };

  const getLastOrderedDate = (productId) => {
    for (const order of orderHistory) {
      if (order.items?.some((orderItem) => orderItem.product_id === productId)) {
        return new Date(order.tstamp);
      }
    }
    return null;
  };

  // Check if user is logged in
  const isAuthenticated = () => {
    return user?.isAuthenticated === true;
  };

  // Get user display name
  const getDisplayName = () => {
    if (!user) return 'Guest';
    return user.name || user.email?.split('@')[0] || 'Guest';
  };

  // Dietary management methods
  const hasDietaryRestriction = (restriction) => {
    return preferences.dietaryRestrictions?.includes(restriction) || false;
  };

  const getAllDietaryRestrictions = () => {
    return preferences.dietaryRestrictions || [];
  };

  const isItemDietaryCompliant = (item) => {
    if (!item || preferences.dietaryRestrictions.length === 0) {
      return { compliant: true, issues: [] };
    }

    const issues = [];
    const itemText = `${item.name} ${item.description || ''}`.toLowerCase();

    // Check each dietary restriction
    preferences.dietaryRestrictions.forEach((restriction) => {
      switch (restriction.toLowerCase()) {
        case 'vegan':
          const nonVegan = ['chicken', 'beef', 'steak', 'pork', 'bacon', 'ham', 'fish', 'shrimp', 'seafood', 'meat', 'cheese', 'milk', 'cream', 'butter', 'egg', 'honey', 'yogurt'];
          nonVegan.forEach((ingredient) => {
            if (itemText.includes(ingredient)) {
              issues.push(`Contains ${ingredient} (not vegan)`);
            }
          });
          break;

        case 'vegetarian':
          const nonVegetarian = ['chicken', 'beef', 'steak', 'pork', 'bacon', 'ham', 'fish', 'shrimp', 'seafood', 'meat'];
          nonVegetarian.forEach((ingredient) => {
            if (itemText.includes(ingredient)) {
              issues.push(`Contains ${ingredient} (not vegetarian)`);
            }
          });
          break;

        case 'gluten-free':
          const gluten = ['wheat', 'flour', 'bread', 'pasta', 'noodle', 'barley', 'rye'];
          gluten.forEach((ingredient) => {
            if (itemText.includes(ingredient)) {
              issues.push(`Contains ${ingredient} (contains gluten)`);
            }
          });
          break;

        case 'nut-free':
          const nuts = ['peanut', 'almond', 'cashew', 'walnut', 'pecan', 'pistachio'];
          nuts.forEach((ingredient) => {
            if (itemText.includes(ingredient)) {
              issues.push(`Contains ${ingredient} (contains nuts)`);
            }
          });
          break;

        case 'dairy-free':
          const dairy = ['milk', 'cheese', 'cream', 'butter', 'yogurt'];
          dairy.forEach((ingredient) => {
            if (itemText.includes(ingredient)) {
              issues.push(`Contains ${ingredient} (contains dairy)`);
            }
          });
          break;
      }
    });

    return {
      compliant: issues.length === 0,
      issues,
    };
  };

  const getDietaryFriendlyRecommendations = (menuItems, limit = 5) => {
    if (!menuItems || menuItems.length === 0) return [];

    // Filter items that meet dietary restrictions
    const compliantItems = menuItems.filter((item) => {
      const compliance = isItemDietaryCompliant(item);
      return compliance.compliant;
    });

    // Score and rank compliant items
    const scored = compliantItems.map((item) => ({
      item,
      score: calculateItemScore(item),
    }));

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, limit).map((s) => s.item);
  };

  const getDietaryPreferenceSummary = () => {
    return {
      restrictions: preferences.dietaryRestrictions || [],
      favoriteCategories: preferences.favoriteCategories || [],
      dislikedItems: preferences.dislikedItems || [],
      spiceLevel: preferences.spiceLevel || null,
      hasRestrictions: (preferences.dietaryRestrictions?.length || 0) > 0,
    };
  };

  const clearDietaryRestrictions = () => {
    setPreferences((prev) => ({
      ...prev,
      dietaryRestrictions: [],
    }));
  };

  return (
    <UserContext.Provider
      value={{
        // State
        user,
        orderHistory,
        favoriteItems,
        preferences,
        tasteProfile,

        // Auth methods
        login,
        logout,
        updateUser,
        isAuthenticated,
        getDisplayName,

        // Order history methods
        addOrderToHistory,
        getRecentOrders,
        getOrdersForBusiness,
        getOrderFrequency,
        getLastOrderedDate,

        // Favorites methods
        addFavoriteItem,
        removeFavoriteItem,
        isFavoriteItem,

        // Preferences methods
        updatePreferences,
        addDietaryRestriction,
        removeDietaryRestriction,
        addFavoriteCategory,

        // Taste profile methods
        updateTasteProfileFromOrder,

        // Personalization methods
        getPersonalizedRecommendations,
        shouldShowItem,
        calculateItemScore,

        // Dietary management methods
        hasDietaryRestriction,
        getAllDietaryRestrictions,
        isItemDietaryCompliant,
        getDietaryFriendlyRecommendations,
        getDietaryPreferenceSummary,
        clearDietaryRestrictions,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
